import { Link } from "react-router-dom";
import {
  Search, Music, Users, Zap, Globe,
  ExternalLink, Sparkles, ArrowRight,
  Headphones, Play, Terminal,
  Cpu, Code2, Video, Mic2, Gift, Link2,
  Disc3, Code, Palette, Heart, Coffee, Mic, Dumbbell,
  BookOpen, Gamepad2, PartyPopper, Lightbulb
} from "lucide-react";
import { CodeBlock } from "../components/CodeBlock";
import { AuroraBackground } from "../components/AuroraBackground";
import { SEO } from "../components/SEO";

const REMOTE_URL = "https://mcp-api.starsinger.ai/mcp";

const QUICKSTART = `{
  "mcpServers": {
    "starsinger": {
      "command": "npx",
      "args": ["-y", "starsinger-mcp"],
      "env": {
        "STARSINGER_API_KEY": "your_api_key_here"
      }
    }
  }
}`;

const EXAMPLE_QUERIES = [
  "Make a song for my sister's 30th birthday — upbeat pop, mention her dog Biscuit",
  "Write and sing a lo-fi piano song about rainy Sunday mornings",
  "Turn that song into a music video starring me",
  "Find me calm instrumental music for studying",
];

const features = [
  {
    icon: Mic2,
    title: "Make a Real Song",
    desc: "make_song writes the lyrics, composes the music and sings it with real vocals — or makes an instrumental. 49 languages, your own lyrics welcome.",
    image: "/demo-assets/purpose_built_tools.jpg",
  },
  {
    icon: Video,
    title: "A Music Video Starring You",
    desc: "make_music_video turns any of your songs into a 2–3 minute video in which you (or an adult who agreed) sing it, from one photo.",
    image: "/demo-assets/works_everywhere.jpg",
  },
  {
    icon: Search,
    title: "Search the Catalog",
    desc: "search_music and similar_music find tracks in the StarSinger catalog by mood, genre, instrument, language or a described vibe.",
    image: "/demo-assets/natural_language_search.jpg",
  },
  {
    icon: Music,
    title: "Playable Results",
    desc: "Songs you make and catalog tracks come back as playable links your agent can share for on-demand listening.",
    image: "/demo-assets/instant_streaming.jpg",
  },
  {
    icon: Users,
    title: "AI Catalog Artists",
    desc: "Browse the catalog's AI artists with bios and discographies, pull trending tracks, and build playlists.",
    image: "/demo-assets/ai_dj.jpg",
  },
  {
    icon: Globe,
    title: "Hosted or Local",
    desc: "Connect the hosted endpoint with OAuth from any assistant that takes a connector URL, or run the npm package with an API key.",
    image: "/demo-assets/trending_discovery.jpg",
  },
];

const mcpClients = [
  { name: "Claude", icon: Cpu, desc: "Custom connector or Desktop" },
  { name: "ChatGPT", icon: Sparkles, desc: "Via connector URL" },
  { name: "Cursor", icon: Code2, desc: "AI-powered code editor" },
  { name: "VS Code", icon: Terminal, desc: "With MCP extensions" },
  { name: "LangChain", icon: Zap, desc: "Python/JS framework" },
  { name: "CrewAI", icon: Users, desc: "Multi-agent systems" },
];

// Catalog size from admin_stats.catalogTotals (~21.8k rows, 2026-09-27).
// Rows are not unique tracks, so this stays a conservative round number.
const stats = [
  { value: "Free", label: "One song a day", sublabel: "Then $1.99 a song" },
  { value: "49", label: "Languages", sublabel: "Sung, not just translated" },
  { value: "20,000+", label: "Catalog songs", sublabel: "Searchable by vibe" },
];

function AnimatedCounter({ value, label, sublabel }: { value: string; label: string; sublabel: string }) {
  return (
    <div className="group text-center px-4">
      <div className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-1 group-hover:scale-105 transition-transform duration-300">
        {value}
      </div>
      <div className="text-sm font-medium text-white/70">{label}</div>
      <div className="text-xs text-white/40 mt-1">{sublabel}</div>
    </div>
  );
}

function FeatureCard({ feature, index }: { feature: typeof features[0]; index: number }) {
  return (
    <div 
      className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.04] transition-all duration-500"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Hover glow effect */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-amber-500/5 to-purple-500/5 pointer-events-none" />
      
      <div className="relative">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-purple-500/20 flex items-center justify-center mb-4 group-hover:from-amber-500/30 group-hover:to-purple-500/30 transition-all duration-300">
          <feature.icon className="w-5 h-5 text-amber-400" />
        </div>
        <h3 className="font-medium text-white mb-2">{feature.title}</h3>
        <p className="text-sm text-white/50 leading-relaxed">{feature.desc}</p>
      </div>
    </div>
  );
}

function ImageFeatureCard({ feature, index }: { feature: typeof features[0]; index: number }) {
  return (
    <div 
      className="group relative rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.04] transition-all duration-500 overflow-hidden"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Image */}
      <div className="relative h-40 overflow-hidden">
        <img 
          src={feature.image} 
          alt={feature.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>
      
      {/* Content */}
      <div className="relative p-6">
        {/* Hover glow effect */}
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-amber-500/5 to-purple-500/5 pointer-events-none" />
        
        <div className="relative">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-purple-500/20 flex items-center justify-center mb-4 group-hover:from-amber-500/30 group-hover:to-purple-500/30 transition-all duration-300">
            <feature.icon className="w-5 h-5 text-amber-400" />
          </div>
          <h3 className="font-medium text-white mb-2">{feature.title}</h3>
          <p className="text-sm text-white/50 leading-relaxed">{feature.desc}</p>
        </div>
      </div>
    </div>
  );
}

function ClientCard({ client }: { client: typeof mcpClients[0] }) {
  return (
    <div className="group flex flex-col items-center p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.04] transition-all duration-300">
      <div className="w-10 h-10 rounded-lg bg-white/[0.05] flex items-center justify-center mb-3 group-hover:bg-white/[0.08] transition-colors">
        <client.icon className="w-5 h-5 text-white/60 group-hover:text-amber-400 transition-colors" />
      </div>
      <span className="font-medium text-sm text-white/80">{client.name}</span>
      <span className="text-xs text-white/40 mt-0.5">{client.desc}</span>
    </div>
  );
}

function DemoVideo() {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-black/40 border border-white/[0.08] shadow-2xl">
      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-purple-500/20 to-amber-500/20 rounded-2xl blur-xl opacity-50" />
      
      <div className="relative">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full aspect-video object-cover"
          poster="/demo-assets/musicverse.jpg"
        >
          <source src="/demo-assets/musicverse.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay with caption */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-sm text-white/80 text-center">
            <span className="text-amber-400 font-medium">The Musicverse</span> —
            the StarSinger catalog, searchable by mood
          </p>
        </div>
      </div>
    </div>
  );
}

export function Landing() {
  return (
    <>
      <SEO pathname="/" />
      <div className="relative">
        <AuroraBackground />

      {/* Announcement Banner */}
      <div className="relative z-10 border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-center gap-2 text-sm">
          <span className="text-white/50">From the makers of</span>
          <a
            href="https://starsinger.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors font-medium"
          >
            StarSinger.ai <ExternalLink className="w-3 h-3" />
          </a>
          <span className="text-white/30 hidden sm:inline">— the AI song generator</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-24 sm:pt-28 sm:pb-32 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.08]">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm text-white/60">Hosted MCP · OAuth</span>
              <span className="text-white/30">|</span>
              <span className="text-sm text-amber-400">One free song a day</span>
            </div>
          </div>

          {/* Main Headline */}
          <div className="text-center mb-12">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-semibold tracking-tight leading-[0.95] mb-6">
              <span className="text-white">Your AI assistant</span>
              <br />
              <span className="gradient-text">can sing now</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/50 max-w-2xl mx-auto leading-relaxed">
              The StarSinger{" "}
              <a
                href="https://modelcontextprotocol.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400 hover:text-amber-300 transition-colors underline underline-offset-4 decoration-amber-400/30 hover:decoration-amber-400"
              >
                MCP
              </a>{" "}
              server lets Claude, ChatGPT, Cursor or any MCP client{" "}
              <strong className="text-white/80">write and sing a finished song</strong> from one sentence,
              turn it into a <strong className="text-white/80">music video starring you</strong>, and
              search a catalog of 20,000+ AI songs.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link
              to="/docs"
              className="btn btn-primary text-base px-8 py-4 rounded-xl"
            >
              <Link2 className="w-4 h-4" />
              Connect your assistant
            </Link>
            <Link
              to="/login"
              className="btn btn-secondary text-base px-8 py-4 rounded-xl"
            >
              <Play className="w-4 h-4" />
              Get a free API key
            </Link>
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 mb-16">
            {stats.map((stat) => (
              <AnimatedCounter key={stat.label} {...stat} />
            ))}
          </div>

          {/* Demo Video */}
          <div className="max-w-4xl mx-auto">
            <DemoVideo />
          </div>
        </div>
      </section>

      {/* Quick Start Section */}
      <section className="relative z-10 py-20 sm:py-24 px-6 border-y border-white/[0.06] bg-white/[0.01]">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-medium tracking-wider text-amber-400 uppercase mb-3">
              <Terminal className="w-3.5 h-3.5" />
              Quick Start
            </div>
            <h2 className="text-2xl sm:text-3xl font-medium text-white">
              Two ways to connect
            </h2>
          </div>

          <div className="space-y-6">
            <div className="min-w-0">
              <p className="text-sm text-white/70 mb-2">
                <strong className="text-white">Hosted (recommended).</strong> Add a custom connector in your
                assistant with this URL and sign in with your StarSinger account (OAuth). Nothing to install.
              </p>
              <CodeBlock code={REMOTE_URL} language="text" title="MCP server URL" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-white/70 mb-2">
                <strong className="text-white">Local.</strong> Run the npm package with a free API key from your
                dashboard — for Claude Desktop, Cursor, VS Code and other config-file clients.
              </p>
              <CodeBlock code={QUICKSTART} language="json" title="claude_desktop_config.json" />
            </div>
          </div>

          {/* Example Queries */}
          <div className="mt-10">
            <p className="text-sm text-white/40 mb-4">Then ask:</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_QUERIES.map((query, i) => (
                <span
                  key={i}
                  className="px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-sm text-white/50 hover:border-amber-500/30 hover:text-white/70 transition-all cursor-default"
                >
                  &ldquo;{query}&rdquo;
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What it costs */}
      <section className="relative z-10 py-20 sm:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-6">
              <Gift className="w-4 h-4" />
              What it costs your users
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white leading-tight mb-6">
              No subscription. Nothing charged in the chat.
            </h2>
            <p className="text-lg text-white/50 leading-relaxed">
              Songs and videos are billed to the user&apos;s own StarSinger account. When something costs money, the
              tool hands back a StarSinger checkout link and the price — your agent never takes a payment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-amber-500/10 to-purple-500/10 border border-amber-500/20">
              <div className="text-4xl font-semibold text-white mb-2">Free</div>
              <p className="text-white/70 mb-1">One song a day</p>
              <p className="text-sm text-white/40">Published to the public StarSinger feed — the tool asks the user first</p>
            </div>
            <div className="p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="text-4xl font-semibold text-white mb-2">$1.99</div>
              <p className="text-white/70 mb-1">Each extra song</p>
              <p className="text-sm text-white/40">Private until the user publishes it</p>
            </div>
            <div className="p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <div className="text-4xl font-semibold text-white mb-2">$19.99</div>
              <p className="text-white/70 mb-1">Music video starring you</p>
              <p className="text-sm text-white/40">Photo, consent and payment on a StarSinger page</p>
            </div>
          </div>
          <p className="text-sm text-white/40 mt-6">
            US prices; the checkout shows the local price. Catalog search is free up to 100 requests a day —{" "}
            <Link to="/pricing" className="text-amber-400 hover:text-amber-300">API plans</Link>.
          </p>
        </div>
      </section>

      {/* MCP Clients Section */}
      <section className="relative z-10 py-20 sm:py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-medium text-white mb-3">
              Works with your favorite AI tools
            </h2>
            <p className="text-white/40">Any client that supports the Model Context Protocol</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {mcpClients.map((client) => (
              <ClientCard key={client.name} client={client} />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-20 sm:py-28 px-6 border-y border-white/[0.06] bg-white/[0.01]">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Built for AI Agents
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4">
              Make music, not just find it
            </h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">
              Tools for making songs and music videos, and for searching and streaming the catalog —{" "}
              <Link to="/docs" className="text-amber-400 hover:text-amber-300">full reference</Link>.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <ImageFeatureCard key={feature.title} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* What Will You Build? */}
      <section className="relative z-10 py-20 sm:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium mb-6">
              <Lightbulb className="w-4 h-4" />
              Inspiration
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4">
              What will you build?
            </h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">
              Startups, side projects, creative experiments. A few ideas to start from.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: Gift,
                title: "Personal Song Gifts",
                pitch: "A birthday, wedding or anniversary song written from a few details about the person — then a music video of them singing it.",
              },
              {
                icon: Mic,
                title: "Podcast Intro Generator",
                pitch: "A unique intro or outro for every episode, made from the topic and the guest.",
              },
              {
                icon: Disc3,
                title: "AI DJ Agent",
                pitch: "An agent that reads the room and picks the next track from the catalog. Feed it time of day, weather, crowd energy.",
              },
              {
                icon: Code,
                title: "Mood-Matched Coding Playlist",
                pitch: "Focus playlists that adapt to what you're building. Debugging = lo-fi, shipping = hype.",
              },
              {
                icon: Palette,
                title: "AI Mixtape Generator",
                pitch: "Users describe a vibe in one sentence; your app builds a shareable playlist.",
              },
              {
                icon: Heart,
                title: "Wellness & Sleep App",
                pitch: "Match calm instrumentals to breathing exercises, journaling or a sleep routine.",
              },
              {
                icon: Coffee,
                title: "Ambiance Engine",
                pitch: "Jazz brunch at 11am, upbeat happy hour at 5pm, quiet dinner at 8pm. One catalog query per mood.",
              },
              {
                icon: Dumbbell,
                title: "Workout Soundtrack",
                pitch: "Warm-up, peak and cooldown playlists that follow the workout plan's energy.",
              },
              {
                icon: BookOpen,
                title: "AI Study Buddy",
                pitch: "Pomodoro timer + genre rotation. 25 min focus (ambient), 5 min break (upbeat).",
              },
              {
                icon: Gamepad2,
                title: "Game Soundtrack API",
                pitch: "Boss fight = intense. Exploration = nature. Victory = uplifting. One search per game state.",
              },
              {
                icon: PartyPopper,
                title: "Party Anthem Maker",
                pitch: "A custom song for the team offsite, the bachelor party or the group chat — in any of 49 languages.",
              },
              {
                icon: Zap,
                title: "Chat-to-Song Bot",
                pitch: "Turn a Discord or Slack conversation into a song everyone can play back.",
              },
            ].map((idea) => (
              <Link
                key={idea.title}
                to="/docs"
                className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-green-500/30 hover:bg-white/[0.04] transition-all duration-300"
              >
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-green-500/5 to-emerald-500/5 pointer-events-none" />
                <div className="relative">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mb-4 group-hover:from-green-500/30 group-hover:to-emerald-500/30 transition-all duration-300">
                    <idea.icon className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="font-medium text-white mb-2 group-hover:text-green-400 transition-colors">{idea.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{idea.pitch}</p>
                  <span className="inline-flex items-center gap-1 mt-3 text-xs text-green-400/60 group-hover:text-green-400 transition-colors">
                    Build this <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Powered By Section */}
      <section className="relative z-10 py-20 sm:py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08]">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Headphones className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-medium text-white mb-3">Powered by StarSinger.ai</h3>
                <p className="text-white/50 mb-6 leading-relaxed">
                  StarSinger is an AI song generator and music app on iOS, Android and the web. Describe a song and it
                  is written, composed and sung for you — in your own voice if you like — and any song can become a
                  music video starring you.
                </p>
                <a
                  href="https://starsinger.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors font-medium"
                >
                  Visit StarSinger.ai <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 sm:py-28 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="relative p-12 sm:p-16 rounded-3xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/[0.08] overflow-hidden">
            {/* Background glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-[100px] pointer-events-none" />

            <div className="relative">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4">
                Start for free
              </h2>
              <p className="text-lg text-white/50 mb-2">
                One free song a day for every user · 100 catalog requests a day
              </p>
              <p className="text-sm text-white/40 mb-10">
                No credit card required
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/login"
                  className="btn btn-primary text-base px-8 py-4 rounded-xl"
                >
                  Create Free Account
                </Link>
                <a
                  href="https://github.com/jupiterspins/starsinger-mcp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary text-base px-8 py-4 rounded-xl"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.2-6.085 8.2-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  View on GitHub
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
